import styled from 'styled-components';
import { InputTable } from '../assets/styles/InputTable';
import { BtnBlue, BtnGhost } from './Button';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { IAuth } from 'customTypes';
import Portal from './Portal';
import ClickableContainer from '../pages/s_cctv/S_cctv1/RealtimeCCTV/ClickableContainer';
import YesOrNoConfirm from './Modal/YesOrNo';
import TableLamp from './TableLamp';
import SelectBox from './SelectBox';
import { COMCD_USE_YN, INIT_USE_YN_A } from '../_constants';
import { useFetchCommonCodeList } from '../services/useSetCodeListInSelectBoxForm';

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  justify-content: flex-end;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
    &.disabled {
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  .header {
    padding: 0.5rem;
    padding-bottom: 0;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    gap: 0.5rem;
    .title {
      flex-shrink: 0;
      width: fit-content;
      font-weight: 600;
      margin-right: 0.5rem;
    }
    .search {
      width: 20rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: var(--text-secondary);
      background-color: var(--tonal-light);
      border: 1px solid var(--outline);
      border-radius: 0.25rem;
      padding: 0 0.5rem;
      input {
        padding: 0;
        font-size: 0.875rem;
        color: var(--text-secondary);
        background-color: var(--tonal-light);
        :focus {
          outline: none;
        }
      }
      .material-symbols-rounded {
        user-select: none;
        font-size: 1.375rem;
        &.close-icon {
          font-variation-settings: 'FILL' 1, 'wght' 500;
          cursor: pointer;
          font-size: 1.25rem;
          border-radius: 0.25rem;
          padding: 0.125rem;
          background-color: var(--tonal);
          &:hover {
            box-shadow: 0 0 0.25rem 0.125rem var(--tonal-deep);
          }
          &:active {
            background-color: var(--tonal-deep);
          }
          &.hidden {
            display: none;
          }
        }
      }
    }
    button {
      flex-shrink: 0;
      padding: 0 0.75rem;
      width: 4.5rem;
      height: 2.5rem;
      font-size: 0.875rem;
      border: 1px solid var(--outline);
      background-color: var(--tonal-light);
      &:hover {
        background-color: var(--tonal-deep);
      }
    }
  }
  .bigo {
    &::before {
      content: '|';
      margin: 0 0.375rem;
      color: var(--text-disabled);
    }
    color: var(--filled-blue-deep);
  }
`;

interface IFormList {
  originData: any[];
  data: any[];
  setData: Dispatch<SetStateAction<any[]>>;
  auth: IAuth;
  selectedIndex: number | null;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
  refetch: () => void;
  warning: boolean;
  onUseYnChange?: (item: any, showAlert: boolean) => void;
  onNewItemClick?: () => void;
  buttonText?: string;
}

const FormList = ({ originData, data, setData, auth, selectedIndex, setSelectedIndex, refetch, warning, onUseYnChange, onNewItemClick, buttonText = '새 서약서 작성' }: IFormList) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLInputElement>(null);

  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true);
  const [selectFilter, setSelectFilter] = useState(INIT_USE_YN_A);
  const [inputFilter, setInputFilter] = useState<string>('');
  const [openModal, setOpenModal] = useState<{ status: boolean; type: string; selectedIndex: number | null }>({ status: false, type: 'confirm', selectedIndex: null });

  useEffect(() => {
    const filteredData = originData.filter((el) => {
      const title = el.title.toLowerCase().includes(inputFilter.toLowerCase());
      const contents = el.contents.toLowerCase().includes(inputFilter.toLowerCase());
      const bigo = el.bigo.toLowerCase().includes(inputFilter.toLowerCase());
      const useYn = selectFilter.USE_YN === 'A' ? true : el.useYn === selectFilter.USE_YN;
      return (title || contents || bigo) && useYn;
    });
    const handler = setTimeout(() => {
      setData(filteredData);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [inputFilter, selectFilter, originData]);

  const handleUseYnClick = (el: any, index: number) => {
    if (warning) {
      setOpenModal({ status: true, type: 'confirm', selectedIndex: index });
    } else if (el.useYn !== 'Y') {
      onUseYnChange?.(el, true);
    }
  };

  if (!data || data === undefined || data.length === undefined) {
    return null;
  }

  return (
    <Root>
      <div className='header'>
        <ClickableContainer
          onClick={() => {
            setInputFilter('');
            setSelectFilter(INIT_USE_YN_A);
          }}
          disabled={inputFilter === '' && selectFilter.USE_YN === 'A'}
          button={<BtnGhost>{t('초기화')}</BtnGhost>}
        />
        <SelectBox
          options={useYnComCdListWithAll}
          defaultOption={selectFilter.cdName}
          state={selectFilter}
          setState={setSelectFilter}
          stateKey={COMCD_USE_YN}
          initiateKey={selectFilter[COMCD_USE_YN]}
          textLabel={t('사용유무')}
        />
        <div className='search'>
          <span className='material-symbols-rounded search-icon'>search</span>
          <input type='text' value={inputFilter} placeholder={t('검색어를 입력해주세요')} onChange={(e) => setInputFilter(e.target.value)} />
          <ClickableContainer button={<span className={`material-symbols-rounded close-icon ${inputFilter.length === 0 ? 'hidden' : ''}`}>close</span>} onClick={() => setInputFilter('')} />
        </div>
      </div>
      <InputTable className='margin-left-05 noHorizontalScroll'>
        <div ref={scrollContainerRef}>
          <div className='thead'>
            <div className='tr'>
              <div className='trCol2p5 flex-center shrink0'>No</div>
              <div className='trColGrow flex-center shrink1'>{t('제목 | 비고')}</div>
              <div className='trCol6 flex-center shrink1'>{t('사용유무')}</div>
            </div>
          </div>
          <div className='table'>
            <div className='tbody'>
              {data?.length > 0 ? (
                data.map((el: any, i: number) => (
                  <div className={`tr ${selectedIndex === i ? 'selected' : ''}`} role='button' tabIndex={0} key={`visitOath_${el.id || i}`} onClick={() => setSelectedIndex(i)}>
                    <div className='trCol2p5 flex-center shrink0'>{i + 1}</div>
                    <div className='trColGrow flex-start shrink1'>
                      {el.title}
                      {el.bigo !== '' && <span className='bigo'>{el.bigo}</span>}
                    </div>
                    <div className='trCol6 flex-center'>
                      <ClickableContainer onClick={() => handleUseYnClick(el, i)} button={<TableLamp use={el.useYn === 'Y'} />} />
                    </div>
                  </div>
                ))
              ) : (
                <div className='noData'>No data.</div>
              )}
            </div>
          </div>
        </div>
      </InputTable>
      {auth.createAuth && (
        <ButtonsWrapper>
          <BtnBlue
            className={selectedIndex === null ? 'disabled' : ''}
            onClick={() => {
              setSelectedIndex(null);
              onNewItemClick?.();
            }}
          >
            {t(buttonText)}
          </BtnBlue>
        </ButtonsWrapper>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.selectedIndex !== null && (
          <YesOrNoConfirm
            mainText={t('사용하는 서약서를 변경하시겠습니까?')}
            subText={t('저장되지 않은 내용은 유실됩니다.')}
            onClickClose={() => setOpenModal({ status: false, type: 'confirm', selectedIndex: null })}
            onClickConfirm={() => {
              onUseYnChange?.(data[openModal.selectedIndex as number], true);
              setOpenModal({ status: false, type: 'confirm', selectedIndex: null });
            }}
          />
        )}
      </Portal>
    </Root>
  );
};

export default FormList;
